import './movement-specimen-card.scss'
import { faCircleNotch, faUndo, faCheckCircle, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { defineComponent, PropType } from 'vue'

import { Movement, MovementTypes, LinkableItemLifecycleStates, SpecimenResource } from '@skinsoft/types'
import { locationSelectors, movementSelectors, specimenSelectors } from '@skinsoft/selectors'
import { ASK_FOR_MISSING_LOCATION, STOP_ASKING_FOR_MISSING_LOCATION_TOPIC } from '@skinsoft/bus'
import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { SpecimenCard } from '@skinsoft/ui'
import { LocationName } from '../../../location/location-name'
import { ErrorRibbon } from './error-ribbon'
import { LocationInput } from './location-input'
import { MOVEMENT_ITEM_CARD_LOCATION_CHANGED, UPDATE_ITEM_MOVEMENT_PAGE } from '@skinsoft/events'

type State = 'wait-for-identifier' | 'wait-for-location' | 'validated'

export const MovementSpecimenCard = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sErrorRibbon: ErrorRibbon,
        sSpecimenCard: SpecimenCard,
        sLocationInput: LocationInput,
        sLocationName: LocationName,
    },
    props: {
        commit: {
            type: Function as PropType<() => void>,
            required: true,
        },
        focused: {
            required: false,
        },
        specimen: {
            type: Object as PropType<SpecimenResource>,
            required: true,
        },
        movement: {
            type: Object as PropType<Movement>,
            required: true,
        },
        rollback: {
            type: Function as PropType<() => void>,
            required: true,
        },
    },

    template: require('./movement-specimen-card.html'),
    data() {
        return {
            contentReady: false,
            encounteredError: false,
            undoIcon: faUndo,
            circleNotchIcon: faCircleNotch,
            playIcon: faCheckCircle,
            checkIcon: faCheck,
            crossIcon: faTimes,
            reverting: false,
            state: 'wait-for-identifier' as State,
            isBeingDeleted: false,
            deleting: false,
            shouldAskMissingLocation: false,
            futureLocation: '',
        }
    },
    computed: {
        movementType(): ReturnType<typeof movementSelectors.movementType> {
            return movementSelectors.movementType(this.movement);
        },

        linkableItemState(): ReturnType<typeof movementSelectors.linkableItemState> {
            return movementSelectors.linkableItemState(this.movement, specimenSelectors.id(this.specimen));
        },

        realTimeLocation(): ReturnType<typeof movementSelectors.linkableItemArrivalLocation> {
            return movementSelectors.linkableItemArrivalLocation(
                this.movement,
                specimenSelectors.id(this.specimen),
            );
        },

        canBeReverted(): boolean | ReturnType<typeof movementSelectors.canLinkableItemTransitionTo> | undefined {
            if (this.linkableItemState === LinkableItemLifecycleStates.SELECTED) {
                return false;
            }

            switch (this.movementType) {
                case MovementTypes.SIMPLE:
                    return movementSelectors.canLinkableItemTransitionTo(
                        this.movement,
                        specimenSelectors.id(this.specimen),
                        LinkableItemLifecycleStates.SELECTED,
                    );

                case MovementTypes.COMPLEX:
                case MovementTypes.RETURN:
                    return (
                        movementSelectors.canLinkableItemTransitionTo(
                            this.movement,
                            specimenSelectors.id(this.specimen),
                            LinkableItemLifecycleStates.SELECTED,
                        ) ||
                        movementSelectors.canLinkableItemTransitionTo(
                            this.movement,
                            specimenSelectors.id(this.specimen),
                            LinkableItemLifecycleStates.IN_TRANSIT,
                        )
                    );
            }
        },
    },
    mounted() {
        this.subscribeToBus(ASK_FOR_MISSING_LOCATION, (value) => {
            this.askForMissingLocation(value.payload)
        })
        this.subscribeToBus(STOP_ASKING_FOR_MISSING_LOCATION_TOPIC, () => {
            this.shouldAskMissingLocation = false
        })
        this.subscribeToFutureLocation()
    },
    methods: {
        triggerError() {
            this.encounteredError = true;
        },

        syncLinkableItemState() {
            switch (this.linkableItemState) {
                case LinkableItemLifecycleStates.SELECTED:
                    this.state = 'wait-for-identifier';
                    break;
                case LinkableItemLifecycleStates.IN_TRANSIT:
                    if (this.movementType === MovementTypes.SIMPLE) {
                        this.state = 'validated';
                    } else {
                        this.state = 'wait-for-location';
                    }
                    break;
                case LinkableItemLifecycleStates.FINALIZED:
                    this.state = 'validated';
                    break;
            }
        },

        async revertSpecimen() {
            this.waitForUserApproval(this.$t('movement.specimenCard.revertPrompt') as string);

            this.reverting = true;

            const movementId = movementSelectors.id(this.movement);
            const linkableItemId = specimenSelectors.id(this.specimen);
            try {
                switch (this.movementType) {
                    case MovementTypes.SIMPLE:
                        await this.$movementDataStore.transitionLinkableItem(
                            movementId,
                            linkableItemId,
                            LinkableItemLifecycleStates.SELECTED,
                        );
                        break;
                    case MovementTypes.COMPLEX:
                    case MovementTypes.RETURN:
                        switch (this.linkableItemState) {
                            case LinkableItemLifecycleStates.IN_TRANSIT:
                                await this.$movementDataStore.transitionLinkableItem(
                                    movementId,
                                    linkableItemId,
                                    LinkableItemLifecycleStates.SELECTED,
                                );
                                break;
                            case LinkableItemLifecycleStates.FINALIZED:
                                await this.$movementDataStore.transitionLinkableItem(
                                    movementId,
                                    linkableItemId,
                                    LinkableItemLifecycleStates.IN_TRANSIT,
                                );
                                await this.$movementDataStore.transitionLinkableItem(
                                    movementId,
                                    linkableItemId,
                                    LinkableItemLifecycleStates.SELECTED,
                                );
                                break;
                        }
                        break;
                }
                this.state = 'wait-for-identifier';
                this.$emit('change')
            } catch (error) {
                // tslint:disable-next-line:no-console
                console.error(error);
            } finally {
                this.reverting = false;
            }
        },

        subscribeToFutureLocation() {
            if(this.specimen) {
                const futureLocationId = specimenSelectors.futureLocation(this.specimen)
                if(futureLocationId) {
                    this.$locationDataStore.fetchOne(futureLocationId).subscribe(location => {
                        this.futureLocation = locationSelectors.fullPath(location, this.getAppLocale())
                    })
                }
            }
        },

        askForMissingLocation(concernedItemsIds: string[]) {
            this.shouldAskMissingLocation = concernedItemsIds.includes(this.specimen.id)
        },

        onLocationChanged(newLocation) {
            if(this.shouldAskMissingLocation) {
                const payload = {
                    itemId: this.specimen.id,
                    changes: {
                        locationItemSchema: {
                            permanentLocation: {
                                permanentLocation: newLocation.id,
                            },
                        },
                    },
                }
                this.$emit(UPDATE_ITEM_MOVEMENT_PAGE, payload)
            } else {
                const payload = {
                    itemId: this.specimen.id,
                    location: newLocation,
                }
                this.$emit(MOVEMENT_ITEM_CARD_LOCATION_CHANGED, payload)
            }
        },

        async startSpecimenTransit() {
            if (this.specimen && this.linkableItemState === LinkableItemLifecycleStates.SELECTED) {
                await this.$movementDataStore.transitionLinkableItem(this.movement.id, this.specimen.id, LinkableItemLifecycleStates.IN_TRANSIT)
                this.$emit('change')
            }
        },

        deleteSpecimen() {
            this.deleting = true
            this.$emit('delete', this.specimen.id)
        },
    },
    watch: {
        focused: {
            handler() {
                this.encounteredError = false;
            },
            immediate: false,
        },

        state: {
            handler(state: State) {
                // as this is not executing on the first render, we are sure this will happen only after a transition
                switch (state) {
                    case 'wait-for-identifier':
                        this.rollback();
                        break;
                    case 'validated':
                        this.commit();
                        break;
                }
            },
            immediate: false,
        },

        specimen: {
            handler() {
                if (this.specimen) {
                    this.syncLinkableItemState()
                    this.subscribeToFutureLocation()
                    this.isBeingDeleted = false
                    this.deleting = false
                }
            },
            immediate: true,
            deep: true,
        },
    },
})
